import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { RequestComment, DocumentAttachment } from '@/types/domain';
import { MessageSquare, Send, User, Trash2, Edit, Paperclip } from 'lucide-react';
import { showToast } from '@/context/NotificationContext';

interface EnterpriseCommentThreadProps {
  comments: RequestComment[];
  attachments?: DocumentAttachment[];
  onAddComment: (text: string, attachmentIds?: string[]) => Promise<void>;
  onUpdateComment?: (commentId: string, text: string) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
  disabled?: boolean;
}

export const EnterpriseCommentThread: React.FC<EnterpriseCommentThreadProps> = ({
  comments,
  attachments = [],
  onAddComment,
  onUpdateComment,
  onDeleteComment,
  disabled = false,
}) => {
  const [newCommentText, setNewCommentText] = useState('');
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const activeComments = comments.filter((c) => c.status !== 'DELETED');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newCommentText.trim();
    if (!trimmed) return;

    try {
      setIsSubmitting(true);
      const attIds = selectedAttachmentId ? [selectedAttachmentId] : [];
      await onAddComment(trimmed, attIds);
      setNewCommentText('');
      setSelectedAttachmentId('');
      showToast('Comment Posted', 'Your note has been added to the collaborative audit thread.', 'success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to post comment.';
      showToast('Action Failed', msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!onUpdateComment || !editText.trim()) return;
    try {
      await onUpdateComment(commentId, editText.trim());
      setEditingCommentId(null);
      showToast('Comment Updated', 'Audit discussion note updated.', 'success');
    } catch {
      showToast('Update Failed', 'Failed to update comment.', 'error');
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!onDeleteComment) return;
    if (confirm('Are you sure you want to delete this comment?')) {
      try {
        await onDeleteComment(commentId);
        showToast('Comment Deleted', 'Comment removed from thread.', 'info');
      } catch {
        showToast('Delete Failed', 'Failed to delete comment.', 'error');
      }
    }
  };

  return (
    <Card className="border border-border shadow-sm">
      <CardHeader className="pb-3 border-b border-border/50 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <span>Audit Collaboration & Discussion Thread ({activeComments.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-6">
        {/* Comment List */}
        {activeComments.length === 0 ? (
          <div className="p-6 text-center text-xs text-foreground-secondary border border-dashed border-border rounded-lg">
            No collaboration comments posted yet. Start the conversation below.
          </div>
        ) : (
          <div className="space-y-4">
            {activeComments.map((cmt) => {
              const isEditing = editingCommentId === cmt.id;
              const attachedDoc = attachments.find((a) => a.id === cmt.attachmentId || cmt.attachmentIds?.includes(a.id));

              return (
                <div
                  key={cmt.id}
                  className="p-3.5 rounded-xl border border-border/60 bg-surface-hover/20 space-y-2 text-xs transition-colors hover:border-border"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                        <User className="h-4 w-4" />
                      </div>
                      <div>
                        <span className="font-bold text-foreground">{cmt.userName}</span>
                        <Badge variant="outline" className="ml-2 text-[10px] py-0 font-mono">
                          {cmt.userRole}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-foreground-secondary">
                      <span className="font-mono text-[10px]">
                        {new Date(cmt.createdAt).toLocaleString()}
                      </span>
                      {cmt.status === 'EDITED' && (
                        <span className="text-[10px] italic text-amber-500">(edited)</span>
                      )}
                      {!disabled && onUpdateComment && !isEditing && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingCommentId(cmt.id);
                            setEditText(cmt.commentText);
                          }}
                          className="p-1 hover:bg-surface-hover rounded text-foreground-secondary hover:text-foreground"
                          title="Edit Comment"
                        >
                          <Edit className="h-3 w-3" />
                        </button>
                      )}
                      {!disabled && onDeleteComment && (
                        <button
                          type="button"
                          onClick={() => handleDelete(cmt.id)}
                          className="p-1 hover:bg-destructive/10 rounded text-destructive"
                          title="Delete Comment"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Comment Text / Edit Input */}
                  {isEditing ? (
                    <div className="space-y-2 pt-1">
                      <textarea
                        className="w-full px-3 py-2 text-xs rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary min-h-[60px]"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                      />
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setEditingCommentId(null)}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={() => handleSaveEdit(cmt.id)}>
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-foreground leading-relaxed pl-9 whitespace-pre-wrap">
                      {cmt.commentText}
                    </p>
                  )}

                  {/* Linked Attachment Badge */}
                  {attachedDoc && (
                    <div className="pl-9 pt-1">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-background border border-border text-[11px] font-mono text-foreground-secondary">
                        <Paperclip className="h-3 w-3 text-primary" />
                        <span>Attached: {attachedDoc.fileName}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Comment Composer Form */}
        {!disabled && (
          <form onSubmit={handleSubmit} className="p-3.5 rounded-xl border border-border/80 bg-background space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground flex items-center justify-between">
                <span>Add Collaboration Note / Comment</span>
                <span className="text-[11px] text-foreground-secondary font-mono">Max 5,000 chars</span>
              </label>
              <textarea
                className="w-full px-3 py-2 text-xs rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary min-h-[75px]"
                placeholder="Enter procurement notes, approver queries, or workflow instructions..."
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
                maxLength={5000}
                disabled={isSubmitting}
              />
            </div>

            {/* Optional Attachment Selector */}
            {attachments.length > 0 && (
              <div className="flex items-center gap-2 text-xs">
                <Paperclip className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="text-foreground-secondary">Link Uploaded Attachment:</span>
                <select
                  className="px-2 py-1 text-xs rounded border border-input bg-background font-mono"
                  value={selectedAttachmentId}
                  onChange={(e) => setSelectedAttachmentId(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">None (Comment only)</option>
                  {attachments.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.fileName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <span className="text-[11px] text-foreground-secondary font-mono">
                {newCommentText.length} / 5000 chars
              </span>
              <Button type="submit" size="sm" isLoading={isSubmitting} disabled={!newCommentText.trim()}>
                <Send className="h-3.5 w-3.5 mr-1.5" /> Post Comment
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};
